import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

export interface DropdownItem {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  danger?: boolean;
  divider?: boolean;
  className?: string;
}

export interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: "left" | "right";
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  align = "right",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;

    const gap = 4;
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth || 224; // 224px default (w-56)
    const menuHeight = menuEl?.offsetHeight || items.length * 40 + 16;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top = rect.bottom + gap;
    // If not enough room below, and more room above, position above trigger
    if (spaceBelow < menuHeight + gap && spaceAbove > spaceBelow) {
      top = rect.top - menuHeight - gap;
    }

    let left = align === "right" ? rect.right - menuWidth : rect.left;

    // Viewport padding to keep fully on screen
    const padding = 8;
    left = Math.max(
      padding,
      Math.min(left, window.innerWidth - menuWidth - padding),
    );
    top = Math.max(
      padding,
      Math.min(top, window.innerHeight - menuHeight - padding),
    );

    return { top, left };
  }, [align, items.length]);

  const updatePosition = useCallback(() => {
    const newCoords = calculatePosition();
    if (newCoords) {
      setCoords(newCoords);
    }
  }, [calculatePosition]);

  // When opening, compute position immediately so there's no layout flash
  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isOpen) {
      const initialCoords = calculatePosition();
      if (initialCoords) {
        setCoords(initialCoords);
      }
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Re-adjust exact position using measured menu element on open, scroll, resize
  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleReposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      // Auto-close if scrolled completely out of viewport
      if (rect.bottom < -20 || rect.top > window.innerHeight + 20) {
        setIsOpen(false);
        return;
      }
      updatePosition();
    };

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (triggerRef.current && triggerRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleItemClick = (e: React.MouseEvent, item: DropdownItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (item.onClick) {
      item.onClick();
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className="flex items-center focus:outline-none"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {trigger}
      </button>

      {/* Dropdown menu rendered in portal to escape any table or overflow container */}
      {isOpen &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
            }}
            className="z-[9999] w-56 bg-surface border border-border rounded-xl shadow-xl py-1.5 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item, index) => {
              if (item.divider) {
                return (
                  <div key={index} className="my-1 border-t border-border" />
                );
              }

              const itemContent = (
                <>
                  {item.icon && (
                    <span className="mr-3 flex-shrink-0">{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                </>
              );

              const itemClasses = `
                w-full px-4 py-2
                flex items-center
                text-sm text-left
                transition-colors cursor-pointer
                ${
                  item.danger
                    ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                    : item.className ||
                      "text-text-primary hover:bg-grey-100 dark:hover:bg-surface-elevated"
                }
              `;

              if (item.href) {
                return (
                  <a
                    key={index}
                    href={item.href}
                    className={itemClasses}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(false);
                    }}
                  >
                    {itemContent}
                  </a>
                );
              }

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  className={itemClasses}
                  onClick={(e) => handleItemClick(e, item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleItemClick(e as any, item);
                    }
                  }}
                >
                  {itemContent}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Dropdown;
